import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { useSelector } from 'react-redux';

import { WAITLIST_PREFERENCE_TYPES } from 'dpl/shared/constants/breeder_app';
import {
  RESULTS_TYPE,
  FILTER_SIZE,
  FILTER_COAT,
  FILTER_TAIL,
  FILTER_GENERATION,
  FILTER_AVAILABILITY,
  FILTER_AGE
} from 'dpl/constants/query_params';
import { RESULTS_TYPES } from 'dpl/puppy_search_results/utils/constants';
import { HEALTH_TESTING_GUIDE_PATH } from 'dpl/shared/constants/urls';
import SmartLink from 'dpl/components/SmartLink';

import GenderFilter from './GenderFilter';
import LocationFilter from './LocationFilter';
import BreederBreedHealthFilter from './BreederBreedHealthFilter';
import MultiSelectChecklistFilter from './MultiSelectChecklistFilter';
import ColorFilter from './ColorFilter';
import PriceFilter from './PriceFilter';

function Divider() {
  return <div className="mv8 bb b--default" />;
}

const { SIZE } = WAITLIST_PREFERENCE_TYPES;

export default function SearchFilters({ className }) {
  const { server, queryParams } = useSelector(state => state);
  const {
    breedSizes,
    breedCoat,
    breedGeneration,
    breedTail,
    breedColors,
    availabilityOptions,
    ageOptions
  } = server;
  const resultsType = queryParams[RESULTS_TYPE] || RESULTS_TYPES.PUPPIES;

  const hasSizePreapplied = Boolean(server.preappliedFilters?.[SIZE]);
  const isPuppiesTabActive = resultsType === RESULTS_TYPES.PUPPIES;
  const shouldShowPuppyFilters = isPuppiesTabActive;
  const shouldShowBreederFilters = !isPuppiesTabActive;

  const shouldDisplaySizeFilter = breedSizes?.length > 0;
  const shouldDisplayCoatTypeFilter = breedCoat?.length > 0;
  const shouldDisplayGenerationFilter = breedGeneration?.length > 0;
  const shouldDisplayTailTypeFilter = breedTail?.length > 0;
  const shouldDisplayColorFilter = breedColors?.length > 0;

  return (
    <div className={classnames('SearchFilters', className)}>
      <LocationFilter title="Location" resultsType={resultsType} />
      {shouldShowPuppyFilters && (
        <>
          <Divider />
          <GenderFilter />
        </>
      )}
      {shouldShowBreederFilters && (
        <>
          <Divider />
          <BreederBreedHealthFilter title="Health testing" />
        </>
      )}
      {shouldDisplaySizeFilter && !hasSizePreapplied && (
        <>
          <Divider />
          <MultiSelectChecklistFilter
            filterTitle="Size"
            filterParam={FILTER_SIZE}
            filterOptions={breedSizes}
          />
        </>
      )}
      {shouldDisplayCoatTypeFilter && (
        <>
          <Divider />
          <MultiSelectChecklistFilter
            filterTitle="Coat type"
            filterParam={FILTER_COAT}
            filterOptions={breedCoat}
          />
        </>
      )}
      {shouldDisplayColorFilter && (
        <>
          <Divider />
          <ColorFilter title="Color" filterOptions={breedColors} />
        </>
      )}
      {shouldShowPuppyFilters && (
        <>
          <Divider />
          <MultiSelectChecklistFilter
            filterTitle="Age"
            filterParam={FILTER_AGE}
            filterOptions={ageOptions}
          />
          <Divider />
          <MultiSelectChecklistFilter
            filterTitle="Go home date"
            filterParam={FILTER_AVAILABILITY}
            filterOptions={availabilityOptions}
          />
          <Divider />
          <PriceFilter title="Price" />
        </>
      )}
      {shouldDisplayGenerationFilter && (
        <>
          <Divider />
          <MultiSelectChecklistFilter
            filterTitle="Generation"
            filterParam={FILTER_GENERATION}
            filterOptions={breedGeneration}
            filterTooltipText="Generation refers to the percentage of each parent dog present in that specific litter."
          />
        </>
      )}
      {shouldShowPuppyFilters && (
        <>
          <Divider />
          <BreederBreedHealthFilter
            title="Health testing"
            subtitle={
              <>
                Health testing is performed on breeding dogs to reduce the risk
                of heritable diseases or conditions.{' '}
                <SmartLink
                  className="stone-700 underline"
                  to={HEALTH_TESTING_GUIDE_PATH}
                  target="_blank"
                >
                  Learn more
                </SmartLink>
              </>
            }
            resultsType={resultsType}
          />
        </>
      )}
      {shouldShowBreederFilters && shouldDisplayTailTypeFilter && (
        <>
          <Divider />
          <MultiSelectChecklistFilter
            filterTitle="Tail type"
            filterParam={FILTER_TAIL}
            filterOptions={breedTail}
            filterTooltipText="Indicates whether or not a dog’s tail has been docked or is natural. For some dog breeds, tail docking, or removing a portion of a dog’s tail, is a standard practice."
          />
        </>
      )}
    </div>
  );
}

SearchFilters.propTypes = {
  className: PropTypes.string
};

SearchFilters.defaultProps = {
  className: 'ph4 pb20'
};
